import { Component, createUniqueId, Show } from 'solid-js';
import { ModusWcSliderCustomEvent } from '@trimble-oss/moduswebcomponents';
import { t } from '@trimble/i18n';

const MAX_OPACITY_PERCENT = 100;

export interface TransparencyRowProps {
  readonly id: string;
  readonly labelKey: string;
  readonly sliderTestId: string;
  readonly min: number;
  readonly percent: number;
  readonly isDisabled?: boolean;
  readonly disabledTooltipKey?: string;
  /**
   * Link state against a shared upstream value (e.g. main-toolbar
   * global). Rows without an upstream leave it undefined and render no
   * link-status indicator.
   */
  readonly isLinked?: boolean;
  onSliderChange(ev: ModusWcSliderCustomEvent<InputEvent>): void;
}

/**
 * One labelled slider row in a visibility menu. Layout only — opacity
 * state and side-effects are owned by the parent. Optionally renders a
 * passive link-status indicator when {@link isLinked} is defined.
 */
export const TransparencyRow: Component<TransparencyRowProps> = (props) => {
  const linkStatusId = createUniqueId();
  const hasLinkStatus = (): boolean => props.isLinked !== undefined;
  const linkStatusText = (): string =>
    t(
      props.isLinked
        ? 'mobileMapping.transparency.linkedToGlobal'
        : 'mobileMapping.transparency.overriddenInPane'
    );

  return (
    <div
      class={`flex flex-col gap-1 ${props.isDisabled ? 'opacity-50' : ''}`}
      data-testid={`visibility-menu-row-${props.id}`}
      title={
        props.isDisabled && props.disabledTooltipKey
          ? t(props.disabledTooltipKey)
          : undefined
      }
    >
      <div class="flex items-center justify-between gap-2">
        <span class="text-sm text-base-content">{t(props.labelKey)}</span>
        <div class="flex items-center gap-2">
          <div class="flex h-5 w-5 items-center justify-center">
            <Show when={hasLinkStatus()}>
              <span
                class="flex items-center justify-center text-base-muted"
                data-testid={`visibility-link-status-${props.id}`}
                data-linked={String(props.isLinked)}
                title={linkStatusText()}
              >
                <i
                  class={`icon-font i16 ${
                    props.isLinked ? 'tc-icon-link' : 'tc-icon-link-off'
                  }`}
                  aria-hidden="true"
                />
              </span>
              <span
                id={linkStatusId}
                class="sr-only"
                textContent={linkStatusText()}
              />
            </Show>
          </div>
          <span class="min-w-[3rem] text-right text-sm text-base-content tabular-nums">
            {props.percent}%
          </span>
        </div>
      </div>
      <div class="trimble-slider slider-horizontal w-full">
        {/* modus-wc-slider copies host ARIA onto its input only once, at
            load, so the id stays fixed and the referenced text changes. */}
        <modus-wc-slider
          attr:data-testid={props.sliderTestId}
          attr:aria-describedby={hasLinkStatus() ? linkStatusId : undefined}
          class="w-full h-6 min-h-6"
          min={props.min}
          max={MAX_OPACITY_PERCENT}
          step={1}
          value={props.percent}
          size="sm"
          disabled={props.isDisabled}
          on:inputChange={(ev) => props.onSliderChange(ev)}
        />
      </div>
    </div>
  );
};
